import React, { useMemo, useState } from 'react';
import axios from 'axios';
import '../css/links-data.css';

const BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';

const pad2 = (value) => String(value).padStart(2, '0');

const getTodayDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

const LinksDataPage = () => {
  const today = useMemo(() => getTodayDate(), []);
  const [startDate, setStartDate] = useState(today);
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(today);
  const [endTime, setEndTime] = useState('23:59');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hourly, setHourly] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [rangeLabel, setRangeLabel] = useState('');
  const [showCountries, setShowCountries] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countryError, setCountryError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    setCountries([]);
    setCountryError('');
    try {
      const start = `${startDate}T${startTime}:00+02:00`;
      const end = `${endDate}T${endTime}:00+02:00`;
      const response = await axios.get(`${BASE_URL}/links/data`, {
        params: {
          start,
          end,
          tz: 'Europe/Paris',
          tzOffset: '+02:00',
        },
      });
      const data = response.data || {};
      setHourly(Array.isArray(data.hourly) ? data.hourly : []);
      setTotalClicks(data.total_clicks || 0);
      setRangeLabel(`${start} → ${end}`);
    } catch (err) {
      setError(
        err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          'Failed to load data.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    setLoadingCountries(true);
    setCountryError('');
    try {
      const start = `${startDate}T${startTime}:00+02:00`;
      const end = `${endDate}T${endTime}:00+02:00`;
      const response = await axios.get(`${BASE_URL}/links/data`, {
        params: {
          start,
          end,
          tz: 'Europe/Paris',
          tzOffset: '+02:00',
          countries: 1,
        },
      });
      const data = response.data || {};
      setCountries(Array.isArray(data.countries) ? data.countries : []);
    } catch (err) {
      setCountryError(
        err.response?.data?.details ||
          err.response?.data?.error ||
          err.message ||
          'Failed to load country data.'
      );
    } finally {
      setLoadingCountries(false);
    }
  };

  return (
    <div className="links-data-page">
      <section className="links-data-hero">
        <div className="links-data-hero-content">
          <p className="links-data-eyebrow">BlinkURL Insights</p>
          <h1>Link Clicks Timeline</h1>
          <p className="links-data-subtitle">
            Choose a date and time range to see hourly click activity, totals, and momentum
            at a glance.
          </p>
        </div>
        <div className="links-data-glow" />
      </section>

      <section className="links-data-panel">
        <div className="links-data-controls">
          <div className="links-data-control">
            <label>Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="links-data-control">
            <label>Start time</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="links-data-control">
            <label>End date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="links-data-control">
            <label>End time</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
          <button className="links-data-btn" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading…' : 'Load stats'}
          </button>
        </div>

        {error && <div className="links-data-error">{error}</div>}

        <div className="links-data-summary">
          <div className="links-data-card">
            <span>Total clicks</span>
            <strong>{totalClicks.toLocaleString()}</strong>
          </div>
          <div className="links-data-card">
            <span>Range</span>
            <strong>{rangeLabel || 'Select a range'}</strong>
          </div>
          <div className="links-data-card">
            <span>Timezone</span>
            <strong>Europe/Paris (+02:00)</strong>
          </div>
        </div>

        <div className="links-data-grid">
          <div className="links-data-table">
            <div className="links-data-table-header">
              <h2>Hourly clicks</h2>
              <p>Each row represents one hour in the selected range.</p>
            </div>
            <div className="links-data-table-body">
              {hourly.length === 0 && !loading ? (
                <div className="links-data-empty">No data yet. Try a different range.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Hour</th>
                      <th>Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourly.map((row) => (
                      <tr key={row.hour}>
                        <td>{row.hour}</td>
                        <td>{row.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="links-data-bars">
            <h2>Momentum</h2>
            <p>Quick visual of click volume by hour.</p>
            <div className="links-data-bar-list">
              {hourly.map((row) => {
                const max = Math.max(...hourly.map((h) => h.clicks), 1);
                const width = Math.round((row.clicks / max) * 100);
                return (
                  <div key={row.hour} className="links-data-bar-row">
                    <span>{row.hour.split(' ')[1]}</span>
                    <div className="links-data-bar">
                      <div style={{ width: `${width}%` }} />
                    </div>
                    <strong>{row.clicks}</strong>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="links-data-countries">
          <button
            type="button"
            className="links-data-toggle"
            onClick={() => {
              const next = !showCountries;
              setShowCountries(next);
              if (next && countries.length === 0) {
                fetchCountries();
              }
            }}
          >
            {showCountries ? 'Hide country breakdown' : 'Expand country breakdown'}
          </button>

          {showCountries && (
            <div className="links-data-country-panel">
              {loadingCountries && <div className="links-data-empty">Loading countries…</div>}
              {countryError && <div className="links-data-error">{countryError}</div>}
              {!loadingCountries && !countryError && (
                <>
                  {countries.length === 0 ? (
                    <div className="links-data-empty">No country data available.</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Country</th>
                          <th>Clicks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countries.map((row) => (
                          <tr key={row.country}>
                            <td>{row.country}</td>
                            <td>{row.clicks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LinksDataPage;
